import { useDescribeStringGroupLazyQuery } from 'src/generated/graphql';

/** The imperative fetch function returned by `useDescribeStringGroupLazyQuery`. */
type DescribeStringGroupFetch = ReturnType<
  typeof useDescribeStringGroupLazyQuery
>[0];

/**
 * Reads one version's CX ID membership. CX IDs are version-level group
 * metadata, not a per-string field, so they come from describeStringGroup
 * rather than the ListStrings snapshot.
 *
 * Fails loudly, unlike DiffModal's inline equivalent, which resolves a failed
 * read to `[]` because there CX IDs are supplementary to a read-only diff. Here
 * the result is SUBMITTED: a swallowed failure would send an empty `cxIds` and
 * silently strip every CX ID from the group on rollback.
 */
export async function fetchVersionCxIds(
  fetchFn: DescribeStringGroupFetch,
  namespaceId: string,
  stringGroupId: string,
  version: number,
): Promise<string[]> {
  const { data, error } = await fetchFn({
    fetchPolicy: 'network-only',
    variables: {
      namespaceId,
      stringGroupId,
      stringGroupVersion: version.toString(),
    },
  });

  if (error) {
    throw error;
  }

  // An absent payload must fail rather than read as "this version targeted no
  // CX IDs" — see the fail-loud note above. A present-but-empty `cxIds` is a
  // legitimate answer and passes through.
  if (!data) {
    throw new Error('The CX ID response did not contain data.');
  }

  return data.describeStringGroup.cxIds ?? [];
}
